import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

const Field = ({ label, value, mono, wide }) => (
  <div className={`${wide ? "col-md-12" : "col-md-3 col-6"} mb-3`}>
    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 14, color: "#1a1f36", fontFamily: mono ? "monospace" : "inherit", fontWeight: mono ? 600 : 400, whiteSpace: "pre-wrap" }}>{value || "—"}</div>
  </div>
);

const Section = ({ title, icon, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border rounded">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB", borderRadius: "4px 4px 0 0" }}
        onClick={() => setOpen(!open)}>
        <span><i className={`bi ${icon} me-2`}></i>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-0 p-3">{children}</div>}
    </div>
  );
};

const STATUS_COLOR = { COMPLETED: "#198754", CANCELLED: "#dc3545", PENDING: "#fd7e14" };
const STATUS_BG = { COMPLETED: "#f0fdf4", CANCELLED: "#fff5f5", PENDING: "#fffbeb" };

export default function ViewTransfer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [activeTab, setActiveTab] = useState("systemNotes");

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  useEffect(() => {
        const userId = localStorage.getItem("userId");
        if (!token || !userId) return;
        fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
    }, [token]);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);

    fetch(`${API_BASE_URL}/api/asset-transfers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 404) throw new Error("Transfer record not found.");
        if (!r.ok) throw new Error("Failed to load transfer record.");
        return r.json();
      })
      .then(data => setTransfer(data))
      .catch(err => {
        toast.error(err.message || "Failed to load transfer record.");
        navigate("/alltransfers");
      })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  if (loading) return (
    <>
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid" style={{ paddingTop: 110 }}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary"></div>
          <div className="text-muted mt-2" style={{ fontSize: 13 }}>Loading transfer record...</div>
        </div>
      </div>
    </>
  );

  if (!transfer) return null;

  const status = transfer.status || "PENDING";

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h4 className="fw-bold mb-0"><i className="bi bi-arrow-left-right me-2"></i>Transfer Detail</h4>
              <span className="badge" style={{ background: STATUS_BG[status] || "#f3f4f6", color: STATUS_COLOR[status] || "#6b7280", border: `1px solid ${STATUS_COLOR[status] || "#d1d5db"}30`, fontSize: 12 }}>
                {status}
              </span>
            </div>
            <div className="d-flex gap-2 mt-1">
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/edittransfer/${transfer.id}`)}>
                 Edit
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>
                 Back
              </button>
            </div>
          </div>
          <div className="d-flex gap-2">
            {transfer.assetId && (
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(`/viewasset/${transfer.assetId}`)}>
                <i className="bi bi-box-seam me-1"></i> View Asset
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={() => navigate("/alltransfers")}>
              <i className="bi bi-list-ul me-1"></i> All Transfers
            </button>
          </div>
        </div>

        <Section title="Asset" icon="bi-laptop">
          <Field label="Asset Code" value={transfer.assetCode} mono />
          <Field label="Asset Name" value={transfer.assetName} />
          <Field label="Condition at Transfer" value={transfer.conditionAtTransfer} />
        </Section>

        <Section title="Parties" icon="bi-people">
          <Field label="From Employee" value={transfer.fromEmployeeName} />
          {/* <Field label="From Dept" value={transfer.fromDepartment} /> */}
          <Field label="To Employee" value={transfer.toEmployeeName} />
          {/* <Field label="To Dept" value={transfer.toDepartment} /> */}
        </Section>

        <Section title="Transfer Details" icon="bi-info-circle">
          <Field label="Transfer Type" value={transfer.transferType} />
          <Field label="Transfer Date" value={transfer.transferDate} />
          <Field label="Status" value={status} />
          <Field label="Reason for Transfer" value={transfer.reasonForTransfer} wide />
          <Field label="Dispatch Details" value={transfer.dispatchDetails} wide />
        </Section>

        {/* System Information Tabs */}
        <ul className="nav nav-tabs mb-0 fw-semibold mt-3" style={{ backgroundColor: navbarColor }}>
          {[["systemNotes", "System Notes"]].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
                style={{
                  color: activeTab === key ? navbarColor : "#fff",
                  backgroundColor: activeTab === key ? "#fff" : "transparent",
                  fontWeight: activeTab === key ? "600" : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
        <div className="border p-3">
          <div className="text-muted small mb-2">
            <strong>Date Created:</strong>{" "}
            {transfer.createdAt ? new Date(transfer.createdAt).toLocaleString() : "—"}
          </div>
          {activeTab === "systemNotes" && (
            <SystemNotes entityType="TRANSFER" entityId={transfer.id} token={token} />
          )}
        </div>
      </div>
    </>
  );
}
